import { useEffect } from "react";
import { aboutInfo } from "@/mocks/content";
import Breadcrumb from "@/components/feature/Breadcrumb";

const orgSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "玫瑰视频",
  url: typeof window !== "undefined" ? window.location.origin : "",
  description: "玫瑰视频是国内领先的高清视频资源聚合平台，提供免费高清在线观看服务。",
  email: "service@meiguivideo.com",
  address: {
    "@type": "PostalAddress",
    addressLocality: "北京",
    addressRegion: "朝阳区",
    addressCountry: "CN",
  },
  contactPoint: {
    "@type": "ContactPoint",
    contactType: "customer service",
    email: "service@meiguivideo.com",
    availableLanguage: "Chinese",
  },
};

const aboutPageSchema = {
  "@context": "https://schema.org",
  "@type": "AboutPage",
  name: "关于玫瑰视频",
  description: "了解玫瑰视频的品牌故事、口碑评价和联系方式。",
  url: typeof window !== "undefined" ? `${window.location.origin}/about` : "",
};

export default function AboutPage() {
  useEffect(() => {
    [orgSchema, aboutPageSchema].forEach((schema) => {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  return (
    <div className="pt-16">
      <div className="max-w-5xl mx-auto px-4 md:px-6 pt-6">
        <Breadcrumb
          items={[
            { label: "首页", to: "/" },
            { label: "关于我们" },
          ]}
        />
      </div>

      <section className="max-w-5xl mx-auto px-4 md:px-6 py-10 md:py-16">
        <div className="text-center mb-14">
          <h1 className="text-3xl md:text-4xl font-bold text-foreground-950 mb-4 font-heading">
            关于玫瑰视频
          </h1>
          <p className="text-foreground-500 max-w-2xl mx-auto leading-relaxed">
            {aboutInfo.brand.intro}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 mb-16">
          <div className="bg-background-100 rounded-2xl p-8">
            <h2 className="text-xl font-semibold text-foreground-900 mb-4 flex items-center gap-2 font-heading">
              <i className="ri-focus-3-line text-primary-500"></i>
              我们的使命
            </h2>
            <p className="text-foreground-600 text-sm leading-relaxed">
              {aboutInfo.brand.mission}
            </p>
          </div>
          <div className="bg-background-100 rounded-2xl p-8">
            <h2 className="text-xl font-semibold text-foreground-900 mb-4 flex items-center gap-2 font-heading">
              <i className="ri-eye-line text-primary-500"></i>
              我们的愿景
            </h2>
            <p className="text-foreground-600 text-sm leading-relaxed">
              {aboutInfo.brand.vision}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-center gap-4 mb-16">
          {aboutInfo.brand.values.map((val) => (
            <span
              key={val}
              className="px-5 py-2.5 bg-primary-100 text-primary-700 text-sm font-medium rounded-full"
            >
              {val}
            </span>
          ))}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-5 mb-16">
          {aboutInfo.stats.map((stat) => (
            <div
              key={stat.label}
              className="text-center p-6 rounded-2xl bg-background-100"
            >
              <p className="text-2xl md:text-3xl font-bold text-primary-500 mb-2">
                {stat.value}
              </p>
              <p className="text-xs text-foreground-500">{stat.label}</p>
            </div>
          ))}
        </div>

        <div className="mb-16">
          <h2 className="text-2xl font-bold text-foreground-950 mb-8 text-center font-heading">
            用户口碑
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {aboutInfo.testimonials.map((t, i) => (
              <div
                key={i}
                className="bg-background-100 rounded-2xl p-6"
              >
                <div className="flex items-center gap-1 mb-3">
                  {Array.from({ length: t.rating }, (_, j) => (
                    <i key={j} className="ri-star-fill text-accent-500 text-sm"></i>
                  ))}
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  "{t.content}"
                </p>
                <div className="flex items-center gap-2">
                  <span className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-500 text-background-50 text-xs font-medium">
                    {t.name[0]}
                  </span>
                  <div>
                    <p className="text-sm font-medium text-foreground-800">{t.name}</p>
                    <p className="text-xs text-foreground-400">{t.location}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-background-100 rounded-2xl p-8">
          <h2 className="text-2xl font-bold text-foreground-950 mb-8 text-center font-heading">
            联系我们
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: "ri-mail-line", label: "电子邮箱", value: aboutInfo.contact.email },
              { icon: "ri-wechat-line", label: "微信公众号", value: aboutInfo.contact.wechat },
              { icon: "ri-weibo-line", label: "官方微博", value: aboutInfo.contact.weibo },
              { icon: "ri-time-line", label: "服务时间", value: aboutInfo.contact.hours },
            ].map((item) => (
              <div key={item.label} className="text-center">
                <span className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-100">
                  <i className={`${item.icon} text-lg text-primary-500`}></i>
                </span>
                <p className="text-xs text-foreground-400 mb-1">{item.label}</p>
                <p className="text-sm font-medium text-foreground-800">{item.value}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}